import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import ContentBox from '../components/ContentBox';
import HabboImage from '../components/HabboImage';
import ZonePicker from '../components/events/ZonePicker';
import EventCard from '../components/events/EventCard';
import AddToCalendar from '../components/events/AddToCalendar';
import {
    addDaysToKey,
    countdownLabel,
    dayKey,
    durationLabel,
    eventDay,
    eventEndAt,
    eventLength,
    groupByDay,
    isLive,
    minutesInDay,
    monthCells,
    offsetLabel,
    pad,
    readStoredZone,
    readableDayLabel,
    readableMonth,
    readableRange,
    shiftMonthKey,
    timeLabel,
    typeColor,
    weekKeys,
    weekStartKey,
    writeStoredZone,
    zonedToUtc
} from '../components/events/eventsPublic';

const API_URL = process.env.REACT_APP_API_URL;

const VIEWS = [
    { key: 'month', label: 'Month' },
    { key: 'week', label: 'Week' },
    { key: 'day', label: 'Day' },
    { key: 'list', label: 'List' }
];

const WEEKDAY_HEADS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const EVENT_TYPES = ['Event Creator', 'Frankly', 'Community'];

const HOUR_HEIGHT = 44;
const HOURS = Array.from({ length: 24 }, (_, index) => index);
const LIST_DAYS = 45;

const RIBBON_BUTTON =
    "border border-gray-300 bg-offwhite px-2 py-1 sm:px-3 font-['Jersey_10'] text-[15px] uppercase leading-none tracking-[1px] text-gray-700 shadow-[0_2px_0_0_rgba(0,0,0,0.2)] transition-colors hover:bg-gold disabled:cursor-not-allowed disabled:opacity-40";

const RIBBON_ACTIVE =
    "border border-gray-300 bg-maroon px-2 py-1 sm:px-3 font-['Jersey_10'] text-[15px] uppercase leading-none tracking-[1px] text-white shadow-[0_2px_0_0_rgba(0,0,0,0.3)]";

const SELECT_CLASS =
    'border border-gray-300 bg-offwhite px-2 py-1 text-[12px] text-gray-700 shadow-[0_2px_0_0_rgba(0,0,0,0.2)] focus:outline-none';

const hourLabel = (hour) => `${pad(hour)}:00`;

const rangeKeys = (view, anchor) => {
    if (view === 'day') return { fromKey: anchor, toKey: addDaysToKey(anchor, 1) };
    if (view === 'list') return { fromKey: anchor, toKey: addDaysToKey(anchor, LIST_DAYS) };

    if (view === 'month') {
        const cells = monthCells(anchor);
        return { fromKey: cells[0].key, toKey: addDaysToKey(cells[cells.length - 1].key, 1) };
    }

    const start = weekStartKey(anchor);
    return { fromKey: start, toKey: addDaysToKey(start, 7) };
};

const geometry = (event, zone) => {
    const startMin = minutesInDay(event.startAt, zone);
    const endMin = minutesInDay(eventEndAt(event), zone);
    const closing = endMin <= startMin ? 1440 : endMin;

    const top = Math.max(0, (startMin / 60) * HOUR_HEIGHT);
    const bottom = (Math.min(closing, 1440) / 60) * HOUR_HEIGHT;

    return { top, height: Math.max(18, bottom - top - 2) };
};

const useNarrow = () => {
    const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 767px)').matches);

    useEffect(() => {
        const query = window.matchMedia('(max-width: 767px)');
        const handle = (event) => setNarrow(event.matches);

        query.addEventListener('change', handle);
        return () => query.removeEventListener('change', handle);
    }, []);

    return narrow;
};

function TypeBadge({ type }) {
    return (
        <span
            className="inline-block border border-black/40 px-[6px] py-[1px] font-['Jersey_10'] text-[13px] uppercase leading-none tracking-[1px] text-white"
            style={{ backgroundColor: typeColor(type) }}
        >
            {type}
        </span>
    );
}

function EventBlock({ event, zone, style, selected, detailed, onSelect }) {
    const live = isLive(event);

    return (
        <button
            type="button"
            onClick={() => onSelect(event)}
            style={{ ...style, backgroundColor: typeColor(event.type) }}
            title={`${event.name} hosted by ${event.host}`}
            className={`absolute left-[2px] right-[2px] overflow-hidden border px-[5px] py-[3px] text-left text-white transition-transform hover:z-20 hover:scale-[1.01] ${
                selected ? 'z-10 border-white ring-2 ring-black' : 'border-black/40'
            } ${live ? 'ring-2 ring-green-500' : ''}`}
        >
            <span className="block truncate font-['Jersey_10'] text-[17px] uppercase leading-none tracking-[1px]">
                {timeLabel(event.startAt, zone)}{live ? ' Live!' : ''}
            </span>
            <span className="block truncate text-[14px] leading-tight">{event.name}</span>
            {detailed && (
                <span className="mt-[2px] block truncate text-[12px] leading-tight opacity-90">
                    {event.host} in {event.room}
                </span>
            )}
        </button>
    );
}

function NowLine({ zone }) {
    const minutes = minutesInDay(new Date().toISOString(), zone);
    const offset = (minutes / 60) * HOUR_HEIGHT;

    return (
        <div className="pointer-events-none absolute left-0 right-0 z-30" style={{ top: offset }}>
            <div className="h-[2px] bg-red-600" />
        </div>
    );
}

function TimeGrid({ days, byDay, zone, todayKey, selectedId, detailed, onSelect, onPickDay }) {
    const bodyHeight = HOURS.length * HOUR_HEIGHT;
    const columns = `54px repeat(${days.length}, minmax(0, 1fr))`;

    return (
        <div className="overflow-x-auto">
            <div className={days.length > 1 ? 'min-w-[640px]' : 'min-w-0'}>
                <div>
                    <div
                        className="grid gap-[2px] bg-offwhite pb-[2px]"
                        style={{ gridTemplateColumns: columns }}
                    >
                        <span />
                        {days.map(key => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => onPickDay(key)}
                                className={`flex flex-col items-center border px-1 py-1 transition-colors ${
                                    key === todayKey
                                        ? 'border-maroon bg-maroon text-white'
                                        : 'border-gray-300 bg-offwhite text-gray-700 hover:bg-gold'
                                }`}
                            >
                                <span className="font-['Jersey_10'] text-[17px] uppercase leading-none tracking-[1px]">
                                    {readableDayLabel(key, { short: true })}
                                </span>
                                <span className="text-[11px] uppercase tracking-wide opacity-70">
                                    {(byDay.get(key) || []).length} events
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="grid gap-[2px]" style={{ gridTemplateColumns: columns }}>
                        <div className="relative" style={{ height: bodyHeight }}>
                            {HOURS.map(hour => (
                                <div
                                    key={hour}
                                    className="absolute right-1 -translate-y-[6px] font-['Jersey_10'] text-[15px] leading-none text-gray-400"
                                    style={{ top: hour * HOUR_HEIGHT }}
                                >
                                    {hourLabel(hour)}
                                </div>
                            ))}
                        </div>

                        {days.map(key => {
                            const entries = byDay.get(key) || [];

                            return (
                                <div
                                    key={key}
                                    className={`relative border border-gray-200 ${key === todayKey ? 'bg-[#fffaf0]' : 'bg-white'}`}
                                    style={{ height: bodyHeight }}
                                >
                                    {HOURS.map(hour => (
                                        <div
                                            key={hour}
                                            className={`absolute left-0 right-0 border-t ${hour % 2 === 0 ? 'border-gray-200' : 'border-gray-100'}`}
                                            style={{ top: hour * HOUR_HEIGHT }}
                                        />
                                    ))}

                                    {key === todayKey && <NowLine zone={zone} />}

                                    {entries.map(event => (
                                        <EventBlock
                                            key={event.id}
                                            event={event}
                                            zone={zone}
                                            detailed={detailed}
                                            selected={event.id === selectedId}
                                            style={geometry(event, zone)}
                                            onSelect={onSelect}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function MonthChip({ event, zone, selected, onSelect }) {
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onSelect(event); }}
            title={`${event.name} hosted by ${event.host}`}
            className={`flex w-full items-center gap-1 overflow-hidden border px-[5px] py-[3px] text-left text-white transition-opacity hover:opacity-85 ${
                selected ? 'border-black ring-1 ring-black' : 'border-black/30'
            }`}
            style={{ backgroundColor: typeColor(event.type) }}
        >
            <span className="shrink-0 font-['Jersey_10'] text-[16px] leading-none tracking-[1px]">
                {timeLabel(event.startAt, zone)}
            </span>
            <span className="truncate text-[13px] leading-tight">{event.name}</span>
        </button>
    );
}

function MonthGrid({ cells, byDay, zone, todayKey, monthKey, selectedId, onSelect, onPickDay }) {
    const rows = Math.ceil(cells.length / 7);

    return (
        <div
            className="grid flex-1 grid-cols-7 gap-[2px]"
            style={{ gridTemplateRows: `auto repeat(${rows}, minmax(104px, 1fr))` }}
        >
            {WEEKDAY_HEADS.map(day => (
                <span
                    key={day}
                    className="py-1 text-center font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-gray-500"
                >
                    {day}
                </span>
            ))}

            {cells.map(cell => {
                const entries = byDay.get(cell.key) || [];
                const inMonth = cell.key.slice(0, 7) === monthKey.slice(0, 7);

                return (
                    <div
                        key={cell.key}
                        role="button"
                        tabIndex={0}
                        onClick={() => onPickDay(cell.key)}
                        onKeyDown={(e) => { if (e.key === 'Enter') onPickDay(cell.key); }}
                        className={`flex cursor-pointer flex-col gap-[2px] overflow-hidden border p-1 transition-colors ${
                            cell.key === todayKey
                                ? 'border-maroon bg-[#fffaf0]'
                                : inMonth
                                    ? 'border-gray-200 bg-white hover:bg-gray-50'
                                    : 'border-gray-100 bg-gray-50 hover:bg-gray-100'
                        }`}
                    >
                        <div className="flex items-baseline justify-between">
                            <span
                                className={`font-['Jersey_10'] text-[18px] leading-none ${
                                    cell.key === todayKey
                                        ? 'bg-maroon px-[5px] py-[2px] text-white'
                                        : inMonth ? 'text-gray-800' : 'text-gray-400'
                                }`}
                            >
                                {cell.day}
                            </span>
                            {entries.length > 0 && (
                                <span className="text-[11px] text-gray-400">{entries.length}</span>
                            )}
                        </div>

                        {entries.slice(0, 3).map(event => (
                            <MonthChip
                                key={event.id}
                                event={event}
                                zone={zone}
                                selected={event.id === selectedId}
                                onSelect={onSelect}
                            />
                        ))}

                        {entries.length > 3 && (
                            <span className="px-[4px] text-[11px] text-gray-500">
                                {entries.length - 3} more
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function AgendaList({ days, byDay, zone, onSelect }) {
    const busy = days.filter(key => (byDay.get(key) || []).length > 0);

    if (busy.length === 0) {
        return (
            <p className="py-6 text-center font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-gray-400">
                Nothing scheduled in the next {LIST_DAYS} days
            </p>
        );
    }

    return (
        <div className="flex flex-col gap-5">
            {busy.map(key => (
                <div key={key}>
                    <div className="mb-2 flex items-baseline gap-2 border-b border-gray-200 pb-1">
                        <span className="font-['Jersey_10'] text-[18px] uppercase tracking-[1px] text-maroon">
                            {readableDayLabel(key)}
                        </span>
                        <span className="text-[11px] uppercase tracking-wide text-gray-400">
                            {(byDay.get(key) || []).length} events
                        </span>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {(byDay.get(key) || []).map(event => (
                            <EventCard key={event.id} event={event} zone={zone} onClick={onSelect} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function DetailPanel({ event, zone, onClose }) {
    if (!event) {
        return (
            <p className="text-[13px] text-gray-500">
                Pick an event from the planner to see the room, the host and the prize.
            </p>
        );
    }

    const live = isLive(event);

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
                <TypeBadge type={event.type} />
                <button
                    type="button"
                    onClick={onClose}
                    className="font-['Jersey_10'] text-[14px] uppercase leading-none tracking-[1px] text-gray-400 hover:text-maroon"
                >
                    Close
                </button>
            </div>

            <div className="font-['Jersey_10'] text-[24px] uppercase leading-none tracking-wide">
                {event.name}
            </div>

            <div className="relative overflow-hidden border border-black/40 bg-[#ece4da] px-2 py-2">
                <div className="font-['Jersey_10'] text-[20px] uppercase leading-none tracking-[1px]">
                    {timeLabel(event.startAt, zone)} to {timeLabel(eventEndAt(event), zone)}
                </div>
                <div className="text-[11px] uppercase tracking-wide text-gray-600">
                    {readableDayLabel(eventDay(event, zone))}
                </div>
                <div className="mt-1 text-[11px] text-gray-500">
                    {timeLabel(event.startAt, 'UTC')} to {timeLabel(eventEndAt(event), 'UTC')} UTC
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-wide text-gray-600">
                    {durationLabel(eventLength(event))}
                    {live ? ', happening now' : `, ${countdownLabel(event)}`}
                </div>

                <HabboImage
                    habbo={event.host}
                    hotel="COM"
                    gesture="sml"
                    direction={4}
                    head_direction={4}
                    className="absolute right-1 bottom-[-18px]"
                    alt=""
                />
            </div>

            <dl className="grid grid-cols-[64px_minmax(0,1fr)] gap-x-2 gap-y-1 text-[12px]">
                <dt className="uppercase tracking-wide opacity-70">Host</dt>
                <dd className="truncate font-bold">{event.host}</dd>

                <dt className="uppercase tracking-wide opacity-70">Room</dt>
                <dd className="truncate">{event.room}</dd>

                <dt className="uppercase tracking-wide opacity-70">Owner</dt>
                <dd className="truncate">{event.owner}</dd>

                <dt className="uppercase tracking-wide opacity-70">Prize</dt>
                <dd className="break-words">{event.win}</dd>
            </dl>

            <AddToCalendar event={event} className="mt-1 self-start" />
        </div>
    );
}

function NextUp({ events, zone, onSelect }) {
    if (!events.length) {
        return <p className="text-[13px] text-gray-500">No events on the books right now.</p>;
    }

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg2:grid-cols-1">
            {events.map((event, index) => (
                <EventCard
                    key={event.id}
                    event={event}
                    zone={zone}
                    muted={index > 0}
                    onClick={onSelect}
                />
            ))}
        </div>
    );
}

function Events() {
    const [zone, setZone] = useState(() => readStoredZone());
    const [chosenView, setChosenView] = useState('month');
    const [anchor, setAnchor] = useState(() => dayKey(new Date(), readStoredZone()));
    const [events, setEvents] = useState([]);
    const [upcoming, setUpcoming] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hostFilter, setHostFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [selectedId, setSelectedId] = useState('');
    const [, setTick] = useState(0);

    const narrow = useNarrow();
    const view = narrow ? 'list' : chosenView;

    const todayKey = dayKey(new Date(), zone);
    const bounds = useMemo(
        () => rangeKeys(view, view === 'list' ? todayKey : anchor),
        [view, anchor, todayKey]
    );

    useEffect(() => {
        const timer = setInterval(() => setTick(value => value + 1), 60000);
        return () => clearInterval(timer);
    }, []);

    const changeZone = (next) => {
        setZone(next);
        writeStoredZone(next);
    };

    const loadRange = useCallback(async () => {
        setLoading(true);

        const from = zonedToUtc(bounds.fromKey, '00:00', zone);
        const to = zonedToUtc(bounds.toKey, '00:00', zone);

        try {
            const response = await fetch(
                `${API_URL}events/schedule?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`
            );
            const data = await response.json();

            if (data.success) {
                setEvents(data.events || []);
            } else {
                toast.error(data.message || 'Failed to load events');
            }
        } catch {
            toast.error('Could not connect to server');
        } finally {
            setLoading(false);
        }
    }, [bounds.fromKey, bounds.toKey, zone]);

    useEffect(() => { loadRange(); }, [loadRange]);

    useEffect(() => {
        (async () => {
            try {
                const response = await fetch(`${API_URL}events/upcoming`);
                const data = await response.json();
                if (data.success) setUpcoming(data.events || []);
            } catch {}
        })();
    }, []);

    const hosts = useMemo(() => {
        const names = new Set(events.map(event => event.host).filter(Boolean));
        return [...names].sort((a, b) => a.localeCompare(b));
    }, [events]);

    const visible = useMemo(() => events.filter(event => {
        if (hostFilter && event.host !== hostFilter) return false;
        if (typeFilter && event.type !== typeFilter) return false;
        return true;
    }), [events, hostFilter, typeFilter]);

    const byDay = useMemo(() => groupByDay(visible, zone), [visible, zone]);

    const cells = useMemo(() => (view === 'month' ? monthCells(anchor) : []), [view, anchor]);

    const days = useMemo(() => {
        if (view === 'day') return [anchor];
        if (view === 'week') return weekKeys(bounds.fromKey);
        if (view === 'month') return cells.map(cell => cell.key);

        return Array.from({ length: LIST_DAYS }, (_, index) => addDaysToKey(bounds.fromKey, index));
    }, [view, anchor, bounds.fromKey, cells]);


    const selected = useMemo(
        () => visible.find(event => event.id === selectedId)
            || upcoming.find(event => event.id === selectedId)
            || null,
        [visible, upcoming, selectedId]
    );

    const shift = (direction) => {
        setAnchor(current => (
            view === 'month'
                ? shiftMonthKey(current, direction)
                : addDaysToKey(current, direction * (view === 'week' ? 7 : 1))
        ));
    };

    const openEvent = (event) => {
        setSelectedId(event.id);
        if (view === 'day' || view === 'week') setAnchor(eventDay(event, zone));
    };

    const heading = view === 'day'
        ? readableDayLabel(anchor)
        : view === 'week'
            ? readableRange(bounds.fromKey, bounds.toKey)
            : view === 'month'
                ? readableMonth(anchor)
                : `Next ${LIST_DAYS} days`;

    return (
        <div className="w-full overflow-x-clip p-4 lg2:p-6">
            <div className="grid grid-cols-1 gap-4 lg2:grid-cols-[320px_minmax(0,1fr)] lg2:gap-6">
                {!narrow && (
                    <div className="min-w-0 lg2:sticky lg2:top-4 lg2:z-20 lg2:self-start lg2:max-h-[calc(100vh-2rem)] lg2:overflow-y-auto lg2:pr-1">
                        <ContentBox theme="default" header="Event Details" icon="img/icons/icon_195.png">
                            <DetailPanel event={selected} zone={zone} onClose={() => setSelectedId('')} />
                        </ContentBox>

                        <ContentBox theme="default" tint="lightskyblue" header="Next Up" icon="img/icons/icon_202.png">
                            <NextUp events={upcoming} zone={zone} onSelect={openEvent} />
                        </ContentBox>
                    </div>
                )}

                <div className="min-w-0">
                    <ContentBox
                        theme="default"
                        tint="#E8871E"
                        header="Events Schedule"
                        icon="img/icons/icon_202.png"
                        headerRight={<ZonePicker zone={zone} onChange={changeZone} />}
                    >
                        <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-gray-200 pb-3">
                            {!narrow && (
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => shift(-1)}
                                        disabled={view === 'list'}
                                        className={RIBBON_BUTTON}
                                    >
                                        &lsaquo;
                                    </button>
                                    <button type="button" onClick={() => setAnchor(todayKey)} className={RIBBON_BUTTON}>
                                        Today
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => shift(1)}
                                        disabled={view === 'list'}
                                        className={RIBBON_BUTTON}
                                    >
                                        &rsaquo;
                                    </button>
                                </div>
                            )}

                            <span className="font-['Jersey_10'] text-[18px] uppercase tracking-[1px] text-maroon">
                                {heading}
                            </span>

                            {!narrow && (
                                <div className="flex w-full items-center justify-between gap-1 sm:ml-auto sm:w-auto sm:justify-end">
                                    {VIEWS.map(entry => (
                                        <button
                                            key={entry.key}
                                            type="button"
                                            onClick={() => setChosenView(entry.key)}
                                            className={view === entry.key ? RIBBON_ACTIVE : RIBBON_BUTTON}
                                        >
                                            {entry.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="mb-3 flex flex-wrap items-center gap-2">
                            <select
                                value={hostFilter}
                                onChange={(e) => setHostFilter(e.target.value)}
                                className={SELECT_CLASS}
                            >
                                <option value="">All hosts</option>
                                {hosts.map(name => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>

                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className={SELECT_CLASS}
                            >
                                <option value="">All event types</option>
                                {EVENT_TYPES.map(name => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>

                            {(hostFilter || typeFilter) && (
                                <button
                                    type="button"
                                    onClick={() => { setHostFilter(''); setTypeFilter(''); }}
                                    className={RIBBON_BUTTON}
                                >
                                    Clear
                                </button>
                            )}

                        </div>

                        {loading ? (
                            <div className="h-[420px] w-full animate-pulse border border-gray-200 bg-gray-100" />
                        ) : view === 'list' ? (
                            <AgendaList
                                days={days}
                                byDay={byDay}
                                zone={zone}
                                onSelect={narrow ? undefined : openEvent}
                            />
                        ) : view === 'month' ? (
                            <div className="flex min-h-[520px] flex-col lg2:min-h-[calc(100vh-320px)]">
                                <MonthGrid
                                    cells={cells}
                                    byDay={byDay}
                                    zone={zone}
                                    todayKey={todayKey}
                                    monthKey={anchor}
                                    selectedId={selectedId}
                                    onSelect={openEvent}
                                    onPickDay={(key) => { setAnchor(key); setChosenView('day'); }}
                                />
                            </div>
                        ) : (
                            <TimeGrid
                                days={days}
                                byDay={byDay}
                                zone={zone}
                                todayKey={todayKey}
                                selectedId={selectedId}
                                detailed={view === 'day'}
                                onSelect={openEvent}
                                onPickDay={(key) => { setAnchor(key); setChosenView('day'); }}
                            />
                        )}

                        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-200 pt-2 text-[11px] uppercase tracking-wide text-gray-500">
                            <span>Times shown in {zone.replace(/_/g, ' ')} ({offsetLabel(zone)})</span>
                            {EVENT_TYPES.map(name => (
                                <span key={name} className="flex items-center gap-1">
                                    <span
                                        className="inline-block h-[10px] w-[10px] border border-black/40"
                                        style={{ backgroundColor: typeColor(name) }}
                                    />
                                    {name}
                                </span>
                            ))}
                        </div>
                    </ContentBox>
                </div>

            </div>
        </div>
    );
}

export default Events;
